import React,{useRef,useEffect,useState} from 'react'
import {MarkerType} from 'reactflow'
import Text from '../../components/Text/Text'
import Icon from '../../components/Text/Icon'
import HorizontalDivider from '../../components/Dividers/HorizontalDivider'
import smoothCurveIcon from '../../assets/icons/smooth-curve.svg'
import stepCurveIcon from '../../assets/icons/step-curve.svg'
import animatedCurveIcon from '../../assets/icons/animated-curve.svg'
import straightCurveIcon from '../../assets/icons/straight-curve.svg'
import arrowCurveIcon from '../../assets/icons/arrow-curve.svg'
import doubleArrowCurveIcon from '../../assets/icons/doublearrow-curve.svg'

function useBoundaryPosition(menuDetails) {
    const menuRef=useRef(null)
    const [position,setPosition]=useState({left: menuDetails.left,top: menuDetails.top})

    useEffect(()=>{
        if(!menuRef.current) return
        const rect=menuRef.current.getBoundingClientRect()
        const newPos={left: menuDetails.left,top: menuDetails.top}
        if(rect.right>window.innerWidth) newPos.left=window.innerWidth-rect.width-8
        if(rect.bottom>window.innerHeight) newPos.top=window.innerHeight-rect.height-8
        if(newPos.left<0) newPos.left=8
        if(newPos.top<0) newPos.top=8
        setPosition(newPos)
    },[menuDetails.left,menuDetails.top])

    return {menuRef,position}
}

const nodeColors=[
    {color: 'white',label: 'White',iconClass: '',icon: 'square'},
    {color: 'gray',label: 'Gray',iconClass: 'text-node-gray',icon: 'square-fill'},
    {color: 'brown',label: 'Brown',iconClass: 'text-node-brown',icon: 'square-fill'},
    {color: 'orange',label: 'Orange',iconClass: 'text-node-orange',icon: 'square-fill'},
    {color: 'yellow',label: 'Yellow',iconClass: 'text-node-yellow',icon: 'square-fill'},
    {color: 'green',label: 'Green',iconClass: 'text-node-green',icon: 'square-fill'},
    {color: 'blue',label: 'Blue',iconClass: 'text-node-blue',icon: 'square-fill'},
    {color: 'purple',label: 'Purple',iconClass: 'text-node-purple',icon: 'square-fill'},
    {color: 'pink',label: 'Pink',iconClass: 'text-node-pink',icon: 'square-fill'},
    {color: 'red',label: 'Red',iconClass: 'text-node-red',icon: 'square-fill'},
]

// ── Custom color picker sub-component ────────────────────────────────────────
const RECENT_KEY='flowsage_recent_colors'
function getRecentColors() {
    try {return JSON.parse(localStorage.getItem(RECENT_KEY)||'[]')} catch {return []}
}
function addRecentColor(hex) {
    const existing=getRecentColors().filter(c=>c!==hex)
    localStorage.setItem(RECENT_KEY,JSON.stringify([hex,...existing].slice(0,8)))
}

function CustomColorPicker({onSelect}) {
    const [hex,setHex]=useState('#6366f1')
    const recent=getRecentColors()
    return (
        <div className='flex flex-col gap-1 p-1'>
            <div className='flex items-center gap-2'>
                <input
                    type='color'
                    value={hex}
                    onChange={e=>setHex(e.target.value)}
                    className='w-8 h-8 rounded cursor-pointer border border-dark-200'
                />
                <input
                    type='text'
                    value={hex}
                    onChange={e=>{if(/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setHex(e.target.value)}}
                    className='w-20 px-1 py-0.5 text-xs rounded border border-dark-200 font-mono'
                />
                <button
                    onClick={()=>{addRecentColor(hex);onSelect(hex)}}
                    className='px-2 py-0.5 text-xs font-semibold rounded bg-primary text-white hover:bg-primary-600'
                >Apply</button>
            </div>
            {recent.length>0&&(
                <div className='flex flex-wrap gap-1 mt-1'>
                    {recent.map(c=>(
                        <button
                            key={c}
                            onClick={()=>{setHex(c);addRecentColor(c);onSelect(c)}}
                            className='w-5 h-5 rounded border border-dark-200 transition-transform hover:scale-110'
                            style={{backgroundColor: c}}
                            title={c}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

// ── Edge Context Menu ─────────────────────────────────────────────────────────
export function EdgeContextMenu({menuDetails,setEdges,setRightClickMenuDetails}) {
    const {menuRef,position}=useBoundaryPosition(menuDetails)
    const selectedEdges=menuDetails.selectedEdges||[]

    const updateSelectedEdges=(updater) => {
        setEdges(edges=>edges.map(edge=>selectedEdges.includes(edge.id)? updater(edge):edge))
    }

    return (
        <div ref={menuRef} className='flex absolute z-50 flex-col justify-center p-2 text-xl rounded-md shadow-md items-left bg-light' style={{left: position.left,top: position.top}}>
            <Text className='text-sm text-zinc-400'>Type</Text>
            <div className='flex items-center w-full justify-left max-w-2tiny'>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,type: 'simplebezier'}))} className='text-sm cursor-pointer select-none'><img src={smoothCurveIcon} alt='Smooth Curve' className='w-12' /></div>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,type: 'step'}))} className='text-sm cursor-pointer select-none'><img src={stepCurveIcon} alt='Step Curve' className='w-12' /></div>
            </div>
            <HorizontalDivider />
            <Text className='text-sm text-zinc-400'>Arrows</Text>
            <div className='flex items-center w-full justify-left max-w-2tiny'>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,markerStart:{type:MarkerType.ArrowClosed,color:'rgba(0,0,0,0)'},markerEnd:{type:MarkerType.ArrowClosed,color:'rgba(0,0,0,0)'}}))} className='text-sm cursor-pointer select-none'><img src={straightCurveIcon} alt='No Arrows' className='w-12' /></div>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,markerStart:{type:MarkerType.ArrowClosed,color:'rgba(0,0,0,0)'},markerEnd:{type:MarkerType.ArrowClosed,color:'#4a4a4d'}}))} className='text-sm cursor-pointer select-none'><img src={arrowCurveIcon} alt='One Arrow' className='w-12' /></div>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,markerStart:{type:MarkerType.ArrowClosed,color:'#4a4a4d'},markerEnd:{type:MarkerType.ArrowClosed,color:'#4a4a4d'}}))} className='text-sm cursor-pointer select-none'><img src={doubleArrowCurveIcon} alt='Double Arrow' className='w-12' /></div>
            </div>
            <HorizontalDivider />
            <Text className='text-sm text-zinc-400'>Dashed</Text>
            <div className='flex items-center w-full justify-left max-w-2tiny'>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,animated:false}))} className='text-sm cursor-pointer select-none'><img src={smoothCurveIcon} alt='Solid Stroke' className='w-12' /></div>
                <div onClick={()=>updateSelectedEdges(edge=>({...edge,animated:true}))} className='text-sm cursor-pointer select-none'><img src={animatedCurveIcon} alt='Dashed Stroke' className='w-12' /></div>
            </div>
            <HorizontalDivider />
            <Text onClick={()=>{
                setEdges(edges=>edges.filter(edge=>!selectedEdges.includes(edge.id)))
                setRightClickMenuDetails(prev=>({...prev,show:false}))
            }} className='text-sm cursor-pointer select-none text-danger'>Remove Edge</Text>
        </div>
    )
}

// ── Node Context Menu ─────────────────────────────────────────────────────────
export function NodeContextMenu({menuDetails,nodes,setNodes,setEdges,setRightClickMenuDetails,onDuplicate,onCopy}) {
    const {menuRef,position}=useBoundaryPosition(menuDetails)
    const selectedNodes=menuDetails.selectedNodes||[]
    const [showColorPicker,setShowColorPicker]=useState(false)

    const setNodeColor=(color) => {
        setNodes(ns=>ns.map(node=>{
            if(!selectedNodes.includes(node.id)) return node
            return {...node,data:{...node.data,color}}
        }))
        setRightClickMenuDetails(prev=>({...prev,show:false}))
    }

    const setCustomColor=(hex) => {
        setNodes(ns=>ns.map(node=>{
            if(!selectedNodes.includes(node.id)) return node
            return {...node,data:{...node.data,color: hex,customColor: hex}}
        }))
    }

    // Bring to Front: set zIndex to max+1
    const bringToFront=() => {
        setNodes(ns=>{
            const maxZ=Math.max(0,...ns.map(n=>n.zIndex||0))
            return ns.map(n=>selectedNodes.includes(n.id)? {...n,zIndex:maxZ+1}:n)
        })
        setRightClickMenuDetails(prev=>({...prev,show:false}))
    }

    // Send to Back: set zIndex to min-1
    const sendToBack=() => {
        setNodes(ns=>{
            const minZ=Math.min(0,...ns.map(n=>n.zIndex||0))
            return ns.map(n=>selectedNodes.includes(n.id)? {...n,zIndex:minZ-1}:n)
        })
        setRightClickMenuDetails(prev=>({...prev,show:false}))
    }

    // Toggle lock (draggable off)
    const toggleLock=() => {
        setNodes(ns=>ns.map(n=>{
            if(!selectedNodes.includes(n.id)) return n
            return {...n,draggable:n.draggable===false? undefined:false,data:{...n.data,locked:n.draggable!==false}}
        }))
        setRightClickMenuDetails(prev=>({...prev,show:false}))
    }

    const isLocked=(nodes||[]).filter(n=>selectedNodes.includes(n.id)).some(n=>n.draggable===false)

    return (
        <div ref={menuRef} className='flex absolute z-50 flex-col justify-center p-2 text-xl rounded-md shadow-md items-left bg-light' style={{left: position.left,top: position.top}}>
            {/* Preset colors */}
            {nodeColors.map(({color,label,iconClass,icon})=>(
                <Text key={color} onClick={()=>setNodeColor(color)} className='flex gap-2 items-center text-sm font-semibold cursor-pointer select-none justify-left'>
                    <Icon className='inline text-xl !px-0' iconClassName={iconClass} icon={icon} />{label}
                </Text>
            ))}

            {/* Custom color picker toggle */}
            <Text onClick={()=>setShowColorPicker(v=>!v)} className='flex gap-2 items-center text-sm font-semibold cursor-pointer select-none justify-left'>
                <Icon className='inline text-xl !px-0' icon='palette' />Custom color…
            </Text>
            {showColorPicker&&<CustomColorPicker onSelect={setCustomColor} />}

            <HorizontalDivider />

            {/* Z-index & lock actions */}
            <Text onClick={bringToFront} className='flex gap-2 items-center text-sm cursor-pointer select-none'>
                <Icon className='inline text-xl !px-0' icon='layers-fill' />Bring to Front
            </Text>
            <Text onClick={sendToBack} className='flex gap-2 items-center text-sm cursor-pointer select-none'>
                <Icon className='inline text-xl !px-0' icon='layers' />Send to Back
            </Text>
            <Text onClick={toggleLock} className='flex gap-2 items-center text-sm cursor-pointer select-none'>
                <Icon className='inline text-xl !px-0' icon={isLocked? 'unlock':'lock'} />{isLocked? 'Unlock':'Lock'}
            </Text>

            <HorizontalDivider />

            {/* Copy / Duplicate */}
            {onCopy&&(
                <Text onClick={()=>{onCopy();setRightClickMenuDetails(prev=>({...prev,show:false}))}} className='flex gap-2 items-center text-sm cursor-pointer select-none'>
                    <Icon className='inline text-xl !px-0' icon='copy' />Copy
                </Text>
            )}
            {onDuplicate&&(
                <Text onClick={()=>{onDuplicate();setRightClickMenuDetails(prev=>({...prev,show:false}))}} className='flex gap-2 items-center text-sm cursor-pointer select-none'>
                    <Icon className='inline text-xl !px-0' icon='files' />Duplicate
                </Text>
            )}

            <HorizontalDivider />

            <Text onClick={()=>{
                setNodes(ns=>ns.filter(n=>!selectedNodes.includes(n.id)))
                setEdges(es=>es.filter(e=>!selectedNodes.includes(e.source)&&!selectedNodes.includes(e.target)))
                setRightClickMenuDetails(prev=>({...prev,show:false}))
            }} className='text-sm cursor-pointer select-none text-danger'>Remove Node</Text>
        </div>
    )
}
